import apis from "@api/api"
import DefaultScreen from "@components/Global/DefaultScreenView"
import { AntDesign, MaterialIcons, Feather } from "@expo/vector-icons"
import React, { useEffect, useState } from "react"
import { Button, H1, H2, View, XStack, Text, YStack, SizableText, Image, H6, H5, H4 } from "tamagui"
import * as Clipboard from 'expo-clipboard';
import { Platform, Pressable, Alert } from "react-native"
import { themeSelector } from "@reduxLocal/selectors/themeSelector"
import LottieView from "@utils/lottieAnimation"
import ErrorBoundary from "@utils/ErrorBoundary"
import styles from "@stylesheet"
import DataSetChart from "@components/dataTables/Charts/DataSetChart"
import SwitchWithIcons from "react-native-switch-with-icons"
import moment from "moment"
import { useQuery } from "@tanstack/react-query"
import { selectUser, userAuths } from "@reduxLocal/selectors/userSelectors"
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
import { useNavigation } from "@react-navigation/native"
import ProductionDataTable from "@components/DataTable/ProductionDataTable"
import { TableColumn } from "@components/DataTable/tableUtils"
import * as Animatable from 'react-native-animatable';
import { useCanProcessStripePayments } from "@hooks/useStripeConnection"
import { useDeviceType } from "@hooks/useDeviceType"
import { Dimensions } from "react-native"

// TypeScript interfaces for production-ready table
interface AffiliateData {
  _id?: string;
  id?: string;
  customerEmail: string;
  subscribedMonth: string;
  totalPayoutCommission: number;
  status: 'active' | 'inactive' | 'pending';
  latestPaidDate: string;
  latestSubscribedData: string;
  country: string;
  totalPayout?: number;
}

interface AffiliateStats {
  totalEarnings: number;
  activeAffiliates: number;
  totalReferrals: number;
}

export const AffiliateComponent = () => {
  const navigation = useNavigation();
  const [copyStatus, setCopyStatus] = useState('');
  const theme = themeSelector();
  const userAuthentications = userAuths();
  const userAffilData = selectUser().affiliateCode?.code;
  const { showConfirm } = useAlertDialog();
  const stripeConnected = useCanProcessStripePayments()

  const { data: getCode, isLoading: codeLoading } = useQuery({
    queryKey: ['affiliateCode'],
    queryFn: async () => {
      const res = await apis.getAffiliateCode();
      return res.data;
    },
  });

  const copyToClipboard = () => {
    Clipboard.setStringAsync(code).then((res) => {
      if (res) {
        setCopyStatus('Link copied to clipboard!');
        setTimeout(() => setCopyStatus(''), 3000);
      }
    })
  };

  const copyUrlToClipboard = () => {
    const url = `https://smartsaas.pro/login?code=${code}`;
    Clipboard.setStringAsync(url).then((res) => {
      if (res) {
        setCopyStatus('URL copied to clipboard!');
        setTimeout(() => setCopyStatus(''), 3000);
      }
    })
  };

  const [code, setCode] = useState(userAffilData || getCode || '');

  useEffect(() => {
    if (getCode) {
      setCode(getCode.toString());
    }
  }, [getCode, codeLoading])

  return (
    <Animatable.View
      animation="fadeInUp"
      duration={600}
      style={{
        flex: 1,
        flexBasis: 300,
        backgroundColor: theme ? "#ffffff" : "#1a1a1a",
        borderRadius: 16,
        padding: 24,
        marginVertical: 16,
        borderWidth: 1,
        borderColor: theme ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)"
      }}
    >
      {Platform.OS !== 'web' && (
        <Animatable.View
          animation="bounceIn"
          delay={200}
          style={{ alignItems: 'center', marginBottom: 20 }}
        >
          <View style={{
            width: 80,
            height: 80,
            backgroundColor: theme ? "#f0f9ff" : "#0f172a",
            borderRadius: 20,
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 12
          }}>
            <MaterialIcons
              name="people"
              size={40}
              color={theme ? "#0ea5e9" : "#38bdf8"}
            />
          </View>
        </Animatable.View>
      )}

      <XStack flex={1} flexWrap="wrap" gap={24} alignItems="center">
        {code ? (
          <YStack flexGrow={1} gap={20} width="100%" alignItems="center">
            <Animatable.View
              animation="pulse"
              iterationCount="infinite"
              duration={2000}
              style={{
                backgroundColor: theme ? "#f0f9ff" : "#1e293b",
                borderRadius: 12,
                padding: 16,
                borderWidth: 1,
                borderColor: theme ? "#0ea5e9" : "#475569",
                minWidth: "100%"
              }}
            >
              <Text
                style={{
                  textAlign: 'center',
                  fontSize: 14,
                  color: theme ? "#64748b" : "#94a3b8",
                  marginBottom: 8,
                  fontWeight: '500'
                }}
              >
                YOUR AFFILIATE CODE
              </Text>
              <H4
                style={{
                  textAlign: 'center',
                  fontWeight: '700',
                  color: theme ? "#0ea5e9" : "#38bdf8",
                  letterSpacing: 2
                }}
              >
                {code}
              </H4>
              {
                !stripeConnected && 
                // Show warning if Stripe is not connected so they can get paid
                <Animatable.View
                  animation="fadeInDown"
                  style={{
                    backgroundColor: theme ? "#fef3c7" : "#451a03",
                    borderRadius: 8,
                    padding: 12,
                    marginTop: 12
                  }}
                >
                  <Text style={{
                    color: theme ? "#9a3412" : "#fca5a1",
                    fontSize: 12,
                    fontWeight: '600'
                  }}>
                    Stripe is not connected. Please connect your Stripe account to receive payments.
                  </Text>
                </Animatable.View>
              }

            </Animatable.View>
            <XStack gap={12} width="100%" flexWrap="wrap">
              <Pressable
                onPress={copyToClipboard}
                style={({ pressed }) => ({
                  flex: 1,
                  minWidth: 140,
                  backgroundColor: pressed
                    ? (theme ? "#e0f2fe" : "#1e3a8a")
                    : (theme ? "#0ea5e9" : "#2563eb"),
                  borderRadius: 12,
                  padding: 16,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transform: [{ scale: pressed ? 0.98 : 1 }]
                })}
              >
                <AntDesign name="copy" size={18} color="white" />
                <Text style={{ color: 'white', fontWeight: '600', fontSize: 14 }}>
                  Copy Code
                </Text>
              </Pressable>

              <Pressable
                onPress={copyUrlToClipboard}
                style={({ pressed }) => ({
                  flex: 1,
                  minWidth: 140,
                  backgroundColor: pressed
                    ? (theme ? "#f0fdf4" : "#14532d")
                    : (theme ? "#10b981" : "#059669"),
                  borderRadius: 12,
                  padding: 16,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  shadowColor: "#10b981",
                  transform: [{ scale: pressed ? 0.98 : 1 }]
                })}
              >
                <Feather name="share" size={18} color="white" />
                <Text style={{ color: 'white', fontWeight: '600', fontSize: 14 }}>
                  Share Link
                </Text>
              </Pressable>
            </XStack>

            {copyStatus && (
              <Animatable.View
                animation="fadeInDown"
                style={{
                  backgroundColor: theme ? "#d4edda" : "#155724",
                  borderRadius: 8,
                  padding: 12,
                  alignSelf: 'center'
                }}
              >
                <Text style={{
                  color: theme ? "#155724" : "#d4edda",
                  fontSize: 12,
                  fontWeight: '600'
                }}>
                  {copyStatus}
                </Text>
              </Animatable.View>
            )}
          </YStack>
        ) : (
          <YStack gap={24} width="100%" alignItems="center">
            <View style={{
              backgroundColor: theme ? "#fef3c7" : "#451a03",
              borderRadius: 12,
              padding: 16,
              borderLeftWidth: 4,
              borderLeftColor: theme ? "#f59e0b" : "#fbbf24",
              width: "100%"
            }}>
              <Text style={{
                fontSize: 14,
                color: theme ? "#92400e" : "#fcd34d",
                lineHeight: 20,
                textAlign: 'center'
              }}>
                {Object.keys(userAuthentications).includes("stripe")
                  ? "Create your very own affiliate code by clicking below. Payments will be processed through your Stripe account."
                  : "A Stripe Connect account is required to receive affiliate payments. Please connect your Stripe integration first."
                }
                </Text>
              </View>

              <Pressable
                onPress={async () => {
                if (Object.keys(userAuthentications).includes("stripe")) {
                  await apis.createAffiliateCode().then((code) => {
                    setCode(code.toString())
                  })
                } else {
                  const res = await showConfirm(
                    'Stripe Connect Required',
                    'You need to connect your Stripe account to create an affiliate code. Would you like to do this now?',
                    { cancelText: 'No', confirmText: 'Yes' }
                  )
                  if (res) {
                    // Use proper navigation typing - replace with your actual navigation method
                    (navigation as any).navigate("Integrations")
                  }
                }
                }}
                style={({ pressed }) => ({
                  backgroundColor: pressed
                    ? (theme ? "#7c3aed" : "#5b21b6")
                    : (theme ? "#8b5cf6" : "#7c3aed"),
                  borderRadius: 12,
                  paddingVertical: 16,
                  paddingHorizontal: 24,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 10,
                  transform: [{ scale: pressed ? 0.96 : 1 }]
                })}
              >
                <AntDesign name="customerservice" size={20} color="white" />
                <Text style={{ color: 'white', fontWeight: '700', fontSize: 16 }}>
                  Request Affiliate Code
                </Text>
              </Pressable>
            </YStack>
        )}
      </XStack>
    </Animatable.View>
  )
}

const AffiliateScreen = () => {
  const theme = themeSelector();
  const { isTablet } = useDeviceType();
  const { width } = Dimensions.get('window');

  // Determine device types based on screen width and platform
  const isMobile = width < 768 || Platform.OS !== 'web';
  const isDesktop = width >= 1024 && Platform.OS === 'web';
  const isTabletDevice = isTablet || (width >= 768 && width < 1024);

  const historyLoading = false;
  const code = selectUser().affiliateCode?.code;

  const [chartData, setChartData] = useState({
    labels: [],
    data: [],
    series: [],
  });

  const [dateRange, setDateRange] = React.useState({
    startDate: moment().subtract(30, 'days').toDate(),
    endDate: new Date(),
  });

  const { data, error, isLoading, isError } = useQuery({
    queryKey: ['affiliateHistory', dateRange],
    queryFn: async () => {
      try {
        const res = await apis.getAffiliateHistory(code, dateRange);
        return res.data;
      } catch (err) {
        console.error('Error fetching affiliate history:', err);
        throw err;
      }
    },
    enabled: !!code,
    retry: 3,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Memoized data processing for better performance
  const processedTableData = React.useMemo(() => {
    if (!Array.isArray(data)) return [];

    return data.map((item: AffiliateData) => ({
      ...item,
      // Ensure proper typing and data validation
      customerEmail: item.customerEmail || 'Unknown',
      subscribedMonth: item.subscribedMonth || 'N/A',
      totalPayoutCommission: Number(item.totalPayoutCommission) || 0,
      status: item.status || 'pending',
      latestPaidDate: item.latestPaidDate || '',
      latestSubscribedData: item.latestSubscribedData || '',
      country: item.country || 'Unknown'
    }));
  }, [data]);

  useEffect(() => {
    if (data && Array.isArray(data)) {
      setTableDate(processedTableData);
      setChartData({
        labels: data.map(item => moment(item.latestPaidDate).format('MMM DD')),
        data: data.map(item => item.totalPayoutCommission || 0),
        series: data.map(item => item.totalPayout || 0),
      });
    }
  }, [data, processedTableData]);

  const extractCountries = React.useCallback((data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map(item => {
      if (item.country) {
        return {
          code: item.country,
          color: theme ? "#0ea5e9" : "#38bdf8",
        };
      }
      return null;
    }).filter(item => item !== null);
  }, [theme]);

  const countriesData = React.useMemo(() => extractCountries(data), [data, extractCountries]);

  const [tableData, setTableDate] = React.useState<AffiliateData[]>([]);

  // Calculate stats with proper typing
  const affiliateStats: AffiliateStats = React.useMemo(() => {
    if (!Array.isArray(data)) {
      return { totalEarnings: 0, activeAffiliates: 0, totalReferrals: 0 };
    }

    return {
      totalEarnings: data.reduce((sum, item) => sum + (item.totalPayoutCommission || 0), 0),
      activeAffiliates: data.filter(item => item.status === 'active').length,
      totalReferrals: data.length
    };
  }, [data]);

  const { totalEarnings, activeAffiliates, totalReferrals } = affiliateStats;

  // Define responsive table columns based on device type
  const tableColumns: TableColumn[] = React.useMemo(() => {
    const baseColumns = [
      {
        key: 'customerEmail',
        title: isMobile ? 'Email' : 'Affiliate Email',
        width: isMobile ? 150 : (isTabletDevice ? 200 : 250),
        sortable: true,
        filterable: true,
        align: 'left' as const,
        render: (value: string) => (
          <Text style={{
            fontSize: isMobile ? 12 : 14,
            color: theme ? '#333' : '#fff',
            fontWeight: '500'
          }}>
            {value || '-'}
          </Text>
        )
      },
      {
        key: 'totalPayoutCommission',
        title: isMobile ? 'Commission' : 'Commission Paid',
        width: isMobile ? 100 : 140,
        sortable: true,
        filterable: true,
        align: 'right' as const,
        render: (value: number) => (
          <Text style={{
            fontSize: isMobile ? 12 : 14,
            color: theme ? '#059669' : '#10b981',
            fontWeight: '600',
            textAlign: 'right'
          }}>
            £{value ? value.toFixed(2) : '0.00'}
          </Text>
        )
      },
      {
        key: 'status',
        title: 'Status',
        width: isMobile ? 80 : 100,
        sortable: true,
        filterable: true,
        align: 'center' as const,
        render: (value: string) => (
          <View style={{
            paddingHorizontal: isMobile ? 4 : 8,
            paddingVertical: 4,
            borderRadius: 12,
            backgroundColor: value === 'active'
              ? (theme ? '#d1fae5' : '#064e3b')
              : (theme ? '#fef3c7' : '#451a03'),
            alignSelf: 'center'
          }}>
            <Text style={{
              fontSize: isMobile ? 10 : 12,
              fontWeight: '600',
              color: value === 'active'
                ? (theme ? '#059669' : '#10b981')
                : (theme ? '#d97706' : '#fbbf24')
            }}>
              {value || 'Unknown'}
            </Text>
          </View>
        )
      }
    ];

    // Add additional columns for tablet and desktop
    if (!isMobile) {
      baseColumns.push(
        {
          key: 'subscribedMonth',
          title: 'Current Month',
          width: 120,
          sortable: true,
          filterable: true,
          align: 'center' as const,
          render: (value: string) => (
            <Text style={{
              fontSize: 12,
              color: theme ? '#666' : '#ccc',
              textAlign: 'center'
            }}>
              {value || '-'}
            </Text>
          )
        },
        {
          key: 'latestPaidDate',
          title: 'Last Payout',
          width: 120,
          sortable: true,
          filterable: true,
          align: 'center' as const,
          render: (value: string) => (
            <Text style={{
              fontSize: 12,
              color: theme ? '#666' : '#ccc',
              textAlign: 'center'
            }}>
              {value ? moment(value).format('MMM DD, YYYY') : '-'}
            </Text>
          )
        }
      );
    }

    // Add more columns for desktop only
    if (isDesktop) {
      baseColumns.push(
        {
          key: 'latestSubscribedData',
          title: 'Date Joined',
          width: 120,
          sortable: true,
          filterable: true,
          align: 'center' as const,
          render: (value: string) => (
            <Text style={{
              fontSize: 12,
              color: theme ? '#666' : '#ccc',
              textAlign: 'center'
            }}>
              {value ? moment(value).format('MMM DD, YYYY') : '-'}
            </Text>
          )
        },
        {
          key: 'country',
          title: 'Country',
          width: 100,
          sortable: true,
          filterable: true,
          align: 'center' as const,
          render: (value: string) => (
            <Text style={{
              fontSize: 12,
              color: theme ? '#666' : '#ccc',
              textAlign: 'center',
              fontWeight: '500'
            }}>
              {value || '-'}
            </Text>
          )
        }
      );
    }

    return baseColumns;
  }, [isMobile, isTabletDevice, isDesktop, theme]);

  return (
    <DefaultScreen
      containerStyle={{
        gap: 24,
      }}
      style={{
        paddingTop: 20,
        paddingBottom: 40,
      }}
    >
      {/* Header with Stats */}
      <XStack flex={1} minHeight={300} width="100%" alignSelf="stretch" flexWrap="wrap" gap={20}>
        <Animatable.View
          animation="fadeInDown"
          duration={800}
          style={{
            backgroundColor: theme ? "#ffffff" : "#1e293b",
            borderRadius: 20,
            padding: 24,
            flex: 1,
            flexBasis: 600,
            borderWidth: 1,
            borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 20 }}>
            <View style={{
              backgroundColor: theme ? "#ddd6fe" : "#312e81",
              borderRadius: 12,
              padding: 12,
              marginRight: 16
            }}>
              <MaterialIcons
                name="trending-up"
                size={28}
                color={theme ? "#7c3aed" : "#a78bfa"}
              />
            </View>
            <View>
              <H2 style={{
                color: theme ? "#1e293b" : "#f1f5f9",
                fontWeight: '700',
                marginBottom: 4
              }}>
                Affiliate Dashboard
              </H2>
              <Text style={{
                color: theme ? "#64748b" : "#94a3b8",
                fontSize: 14
              }}>
                Track your referrals and earnings
              </Text>
            </View>
          </View>

          {/* Stats Grid */}
          <XStack gap={16} flexWrap="wrap" justifyContent="space-between">
            {[
              {
                title: 'Total Earnings',
                value: `£${totalEarnings.toFixed(2)}`,
                icon: 'attach-money',
                color: theme ? "#059669" : "#10b981",
                bgColor: theme ? "#d1fae5" : "#064e3b"
              },
              {
                title: 'Active Affiliates',
                value: activeAffiliates.toString(),
                icon: 'people',
                color: theme ? "#0ea5e9" : "#38bdf8",
                bgColor: theme ? "#dbeafe" : "#0c4a6e"
              },
              {
                title: 'Total Referrals',
                value: totalReferrals.toString(),
                icon: 'group-add',
                color: theme ? "#7c3aed" : "#a78bfa",
                bgColor: theme ? "#ede9fe" : "#312e81"
              }
            ].map((stat, index) => (
              <Animatable.View
                key={index}
                animation="bounceIn"
                delay={index * 200}
                style={{
                  flex: 1,
                  minWidth: 160,
                  backgroundColor: stat.bgColor,
                  borderRadius: 16,
                  padding: 20,
                  alignItems: 'center',
                  borderWidth: 1,
                  borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
                }}
              >
                <MaterialIcons
                  name={stat.icon as any}
                  size={32}
                  color={stat.color}
                  style={{ marginBottom: 8 }}
                />
                <Text style={{
                  fontSize: 24,
                  fontWeight: '800',
                  color: stat.color,
                  marginBottom: 4
                }}>
                  {stat.value}
                </Text>
                <Text style={{
                  fontSize: 12,
                  fontWeight: '600',
                  color: theme ? "#64748b" : "#94a3b8",
                  textAlign: 'center'
                }}>
                  {stat.title}
                </Text>
              </Animatable.View>
            ))}
          </XStack>
        </Animatable.View>
        <AffiliateComponent />
      </XStack>
      {/* Main Content Grid */}
      <XStack
        gap={24}
        flexWrap={isMobile ? 'wrap' : (isTabletDevice ? 'wrap' : 'nowrap')}
        alignItems="flex-start"
        alignSelf="stretch"
        width="100%"
        minHeight={800}
        flex={3}
      >
      {/* Table Section */}
      <Animatable.View
        animation="fadeInUp"
        duration={800}
        delay={600}
        style={{
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderRadius: 20,
          flex: isMobile ? 1 : 3,
          minWidth: isMobile ? "100%" : (isTabletDevice ? "100%" : "65%"),
          borderWidth: 1,
          height: isMobile ? "auto" : "100%",
          borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
          overflow: 'hidden'
        }}
      >
        <View style={{
          padding: 24,
          borderBottomWidth: 1,
          borderBottomColor: theme ? "#e2e8f0" : "#334155"
        }}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <View style={{
              backgroundColor: theme ? "#fef3c7" : "#451a03",
              borderRadius: 8,
              padding: 8,
              marginRight: 12
            }}>
              <MaterialIcons
                name="table-chart"
                size={20}
                color={theme ? "#f59e0b" : "#fbbf24"}
              />
            </View>
            <Text style={{
              fontSize: 20,
              fontWeight: '700',
              color: theme ? "#1e293b" : "#f1f5f9"
            }}>
              Active Affiliates
            </Text>
          </View>
          <Text style={{
            fontSize: 14,
            color: theme ? "#64748b" : "#94a3b8",
            marginTop: 4
          }}>
            Detailed breakdown of your affiliate network
          </Text>
        </View>

        <View style={{ padding: 24, minHeight: 300 }}>
            {isError ? (
              <View style={{
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
                padding: 40,
                backgroundColor: theme ? '#fef2f2' : '#2d1b1b',
                borderRadius: 12,
                borderWidth: 1,
                borderColor: theme ? '#fecaca' : '#7f1d1d'
              }}>
                <MaterialIcons
                  name="error-outline"
                  size={48}
                  color={theme ? '#dc2626' : '#f87171'}
                  style={{ marginBottom: 16 }}
              />
              <Text style={{
                  fontSize: 16,
                  fontWeight: '600',
                  color: theme ? '#dc2626' : '#f87171',
                  textAlign: 'center',
                  marginBottom: 8
              }}>
                  Failed to load affiliate data
                </Text>
                <Text style={{
                  fontSize: 14,
                  color: theme ? '#7f1d1d' : '#fca5a5',
                  textAlign: 'center',
                  marginBottom: 16
                }}>
                  {error?.message || 'An unexpected error occurred'}
              </Text>
                <Button
                  size="$3"
                  variant="outlined"
                  onPress={() => window.location.reload()}
                  backgroundColor={theme ? '#dc2626' : '#7f1d1d'}
                  borderColor={theme ? '#dc2626' : '#7f1d1d'}
                  color="white"
                >
                  Retry
                </Button>
            </View>
          ) : (
                <ProductionDataTable
                  data={processedTableData}
                  columns={tableColumns}
                  loading={isLoading}
                  onRowClick={(row: AffiliateData) => {
                    console.log('Affiliate row clicked:', row);
                    // TODO: Add navigation to affiliate details or modal
                    // navigation.navigate('AffiliateDetails', { affiliateId: row._id || row.id });
                  }}
                  onSelectionChange={(selectedRows: AffiliateData[]) => {
                    console.log('Selected affiliates:', selectedRows);
                    // TODO: Add bulk action logic for selected affiliates
                  }}
                  onBulkAction={(action: string, selectedRows: AffiliateData[]) => {
                    console.log('Bulk action:', action, selectedRows);
                    // TODO: Implement bulk actions like export, delete, update status
                    switch (action) {
                      case 'export':
                        // Handle export logic
                        break;
                      case 'update_status':
                        // Handle status update logic
                        break;
                      default:
                        console.log('Unknown bulk action:', action);
                    }
                  }}
                  enableSelection={!isMobile}
                  enableSorting={true}
                  enableFiltering={!isMobile}
                  enableExport={!isMobile}
                  enableInlineActions={!isMobile}
                  pageSize={isMobile ? 10 : (isTabletDevice ? 20 : 25)}
                  emptyStateMessage="No affiliate data available. Start by creating your affiliate code above."
                  showRowNumbers={false}
                  stickyHeader={!isMobile}
                  style={{
                    borderRadius: 12,
                    borderWidth: 1,
                    minHeight: 600,
                    borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
                  }}
                />
          )}
        </View>
      </Animatable.View>
        {/* Right Column - Chart and Map */}
        {/* <YStack flex={1} gap={24} style={{
          minWidth: isMobile ? undefined : (isTabletDevice ? 300 : 400),
          width: isMobile ? '100%' : undefined
        }}>
          <Animatable.View
            animation="fadeInLeft"
            duration={800}
            delay={200}
            style={{
              backgroundColor: theme ? "#ffffff" : "#1e293b",
              flex: 4,
              borderRadius: 20,
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 12 },
              shadowOpacity: theme ? 0.08 : 0.25,
              shadowRadius: 24,
              elevation: 12,
              borderWidth: 1,
              borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
              overflow: 'hidden'
            }}
          >
            <ErrorBoundary>
              <DataSetChart
                defaultDates={dateRange}
                minHeight={isMobile ? 250 : 400}
                dateCallback={(date) => setDateRange(date)}
                rangeControls={!isMobile}
                dotLabel='Income'
                controls={<></>}
                title={isMobile ? 'Earnings' : 'Affiliate Earnings Over Time'}
                subheader={isMobile ? 'Commission tracking' : 'Track your commission earnings over time'}
                chartTitle='Commission Earnings'
                chart={{
                  labels: chartData.labels || [],
                  colors: [theme ? "#0ea5e9" : "#38bdf8"],
                  options: {
                    theme: {
                      mode: theme ? 'light' : 'dark'
                    }
                  },
                  series: [
                    {
                      name: 'Commission Earned',
                      data: chartData.data || [],
                      fill: 'gradient'
                    },
                  ],
                }}
              />
            </ErrorBoundary>
          </Animatable.View>

          {!isMobile && (
            <Animatable.View
              animation="fadeInRight"
              duration={800}
              delay={400}
              style={{
              flex: isTabletDevice ? 1 : 2,
              alignSelf: 'stretch',
                minWidth: isTabletDevice ? 300 : 350,
                minHeight: isTabletDevice ? 250 : 300,
                backgroundColor: theme ? "#ffffff" : "#1e293b",
                borderRadius: 20,
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 12 },
                shadowOpacity: theme ? 0.08 : 0.25,
                shadowRadius: 24,
                elevation: 12,
                borderWidth: 1,
                borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
                overflow: 'hidden'
              }}
            >
          <View style={{
            padding: 20,
            borderBottomWidth: 1,
            borderBottomColor: theme ? "#e2e8f0" : "#334155"
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View style={{
                backgroundColor: theme ? "#f0f9ff" : "#0f172a",
                borderRadius: 8,
                padding: 8,
                marginRight: 12
              }}>
                <MaterialIcons
                  name="public"
                  size={20}
                  color={theme ? "#0ea5e9" : "#38bdf8"}
                />
              </View>
              <Text style={{
                fontSize: 18,
                fontWeight: '700',
                color: theme ? "#1e293b" : "#f1f5f9"
              }}>
                Global Reach
              </Text>
            </View>
            <Text style={{
              fontSize: 14,
              color: theme ? "#64748b" : "#94a3b8",
              marginTop: 4
            }}>
              Countries with active referrals
            </Text>
          </View>

              <View style={{ flex: 1, padding: 20, borderRadius: 12, overflow: 'hidden' }}>
                <ErrorBoundary>
                  <MapComponent
                    data={countriesData}
                    markers={countriesData}
                  />
                </ErrorBoundary>
              </View>
            </Animatable.View>
          )}
        </YStack> */}

      </XStack>

    </DefaultScreen>
  )
}
export default AffiliateScreen